import {ref} from 'vue';
import {useProjectInformationStore} from '@common/stores';
import {useRoute} from '@typed-router';
import {defineStore} from 'pinia';
import {useLogger, useTranslation} from '#imports';
import {withCleanup} from '@malt/utils';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import type {ClientCompanyMemberResource, ProjectManagerUpdateParam} from '@api';
import {clientCompanyEndpoints, projectInformationEndpoints} from '@/registry';

export const useCompanyMembersStore = defineStore('company-members', () => {
    const logger = useLogger();
    const {t} = useTranslation();

    const loadingCompanyMembers = ref(false);

    const companyMembers = ref<ClientCompanyMemberResource[]>();

    const projectInformationStore = useProjectInformationStore();

    async function fetchCompanyMembers(companyId: string) {
        try {
            loadingCompanyMembers.value = true;
            const response = await clientCompanyEndpoints.getClientCompanyMembers({companyId});
            companyMembers.value = response.members;
        } catch (e) {
            logger.error(e);
            companyMembers.value = null as unknown as ClientCompanyMemberResource[];
        } finally {
            loadingCompanyMembers.value = false;
        }
    }

    function constructMessageAndHeader(projectManagerUpdateParam: ProjectManagerUpdateParam) {
        let message = '';
        let header = '';

        if (projectManagerUpdateParam.newIdentityId) {
            const newManager = companyMembers.value?.find((member) => member.identityId === projectManagerUpdateParam.newIdentityId);
            const newManagerName = `${newManager?.firstName} ${newManager?.lastName}`;
            header = t('projectclient.projectview.sidepanel.team.dialog.notification.selection.success.header');
            message = t('projectclient.projectview.sidepanel.team.dialog.notification.selection.success.message', {0: newManagerName});
        } else if (projectManagerUpdateParam.invitationEmail) {
            header = t('projectclient.projectview.sidepanel.team.dialog.notification.email.success.header');
            message = t('projectclient.projectview.sidepanel.team.dialog.notification.email.success.message', {
                0: projectManagerUpdateParam.invitationEmail,
            });
        }

        return {message, header};
    }

    async function updateProjectOwner(projectManagerUpdateParam: ProjectManagerUpdateParam, projectId: string): Promise<void> {
        try {
            await projectInformationEndpoints.updateProjectManager({projectId, projectManagerUpdateParam});
            await projectInformationStore.fetchProjectInformation(projectId);
            const {message, header} = constructMessageAndHeader(projectManagerUpdateParam);

            pushVJoySnackbar({
                props: {
                    message,
                    header,
                    level: 'success',
                },
            });
        } catch (e) {
            logger.error(e);
            pushVJoySnackbar({
                props: {
                    header: t('projectclient.projectview.sidepanel.team.dialog.notification.error.header'),
                    message: t('projectclient.projectview.sidepanel.team.dialog.notification.error.message'),
                    level: 'error',
                },
            });
        }
    }
    return withCleanup({
        fetchCompanyMembers,
        updateProjectOwner,
        loadingCompanyMembers,
        companyMembers,
    });
});
